import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Filter,
  Loader2,
  MessageSquare,
  MoreVertical,
  Pencil,
  Star,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { useToast } from "@/components/shared/Toast";
import { useNotificationEvent } from "@/context/NotificationContext";
import { useSharedSessions } from "@/context/SharedSessionsContext";
import { buildChatSessionPath } from "@/lib/chatNavigation";
import { chatService, tagService } from "@/lib/services";
import type { ChatSession, TagSummary } from "@/types";

interface ChatHistorySidebarProps {
  currentSessionId?: string;
  /** Increment this value to trigger a session list refresh. */
  refreshTrigger?: number;
  /** Called when a session is deleted, with the deleted session's UUID. */
  onSessionDeleted?: (uuid: string) => void;
  /** When set, only show sessions scoped to this project. */
  projectUuid?: string | null;
}

export function ChatHistorySidebar({
  currentSessionId,
  refreshTrigger,
  onSessionDeleted,
  projectUuid,
}: Readonly<ChatHistorySidebarProps>) {
  const navigate = useNavigate();
  const { sharedSessions, isLoading: isLoadingShared } = useSharedSessions();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showShared, setShowShared] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [availableTags, setAvailableTags] = useState<TagSummary[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);
  const isCommittingRef = useRef(false);
  const [titleUpdateCount, setTitleUpdateCount] = useState(0);

  // Load available tags once
  useEffect(() => {
    tagService
      .list({ pageSize: 100 })
      .then((res) => {
        setAvailableTags(res.data);
      })
      .catch((err) => {
        console.error("Failed to load tags:", err);
      });
  }, []);

  useEffect(() => {
    loadSessions();
  }, [refreshTrigger, activeTags.size, projectUuid, titleUpdateCount]);

  useNotificationEvent("chat_title_updated", () => setTitleUpdateCount((count) => count + 1));

  const loadSessions = async () => {
    try {
      // Only show spinner on initial load, not on background refreshes
      if (!hasLoadedRef.current) {
        setIsLoading(true);
      }
      const params: { pageSize: number; tags?: string; projectUuid?: string; excludeProjectSessions?: boolean } = {
        pageSize: 20,
      };
      if (activeTags.size > 0) {
        params.tags = Array.from(activeTags).join(",");
      }
      if (projectUuid) {
        params.projectUuid = projectUuid;
      } else {
        params.excludeProjectSessions = true;
      }
      const ownedRes = await chatService.listSessions(params);
      setSessions(ownedRes.data);
      hasLoadedRef.current = true;
    } catch (err) {
      console.error("Failed to load sessions:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTag = useCallback((tagUuid: string) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagUuid)) {
        next.delete(tagUuid);
      } else {
        next.add(tagUuid);
      }
      return next;
    });
  }, []);

  const handleSessionClick = (sessionUuid: string, projectUuid?: string | null) => {
    navigate(buildChatSessionPath(sessionUuid, projectUuid));
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => setMenuOpenId(null);
    if (menuOpenId) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [menuOpenId]);

  // Focus the edit input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  const startRename = (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    setMenuOpenId(null);
    isCommittingRef.current = false;
    setEditingId(session.uuid);
    setEditingTitle(session.title || "");
  };

  const commitRename = async (sessionUuid: string) => {
    // Ref-based guard: prevents double-fire from Enter + onBlur.
    // React state (editingId) is async, so checking it doesn't work
    // for synchronous onBlur calls during the same render cycle.
    if (isCommittingRef.current) return;
    isCommittingRef.current = true;

    const trimmed = editingTitle.trim();
    setEditingId(null);
    setEditingTitle("");

    if (!trimmed) {
      isCommittingRef.current = false;
      return;
    }

    try {
      await chatService.updateSession(sessionUuid, { title: trimmed });
      setSessions((prev) => prev.map((s) => (s.uuid === sessionUuid ? { ...s, title: trimmed } : s)));
    } catch (err) {
      console.error("Failed to rename session:", err);
      showToast("Failed to rename chat", "error");
    } finally {
      isCommittingRef.current = false;
    }
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingTitle("");
  };

  const handleToggleStar = async (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    setMenuOpenId(null);
    try {
      await chatService.updateSession(session.uuid, { isStarred: !session.isStarred });
      // Bump updatedAt so the session moves to the top of its new group
      // (starred or unstarred) per sortedSessions ordering.
      const now = new Date().toISOString();
      setSessions((prev) =>
        prev.map((s) => (s.uuid === session.uuid ? { ...s, isStarred: !s.isStarred, updatedAt: now } : s)),
      );
      showToast(session.isStarred ? "Chat unstarred" : "Chat starred", "success");
    } catch (err) {
      console.error("Failed to toggle star:", err);
      showToast("Failed to update chat", "error");
    }
  };

  // Starred sessions first, then unstarred; each group ordered by recency
  // (most recently updated first). Deriving from state means star/unstar
  // re-sorts automatically once the toggled session's timestamp is bumped.
  const sortedSessions = useMemo(() => {
    const recency = (s: ChatSession) => new Date(s.updatedAt || s.createdAt).getTime();
    return [...sessions].sort((a, b) => {
      if (a.isStarred !== b.isStarred) return a.isStarred ? -1 : 1;
      return recency(b) - recency(a);
    });
  }, [sessions]);

  const handleDelete = async (e: React.MouseEvent, session: ChatSession) => {
    e.stopPropagation();
    setMenuOpenId(null);

    const confirmed = await confirm({
      title: "Delete Chat",
      message: `Are you sure you want to delete "${session.title || "this chat"}"? This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });

    if (!confirmed) return;

    try {
      await chatService.deleteSession(session.uuid);
      setSessions((prev) => prev.filter((s) => s.uuid !== session.uuid));
      showToast("Chat deleted", "success");
      onSessionDeleted?.(session.uuid);
    } catch (err) {
      console.error("Failed to delete session:", err);
      showToast("Failed to delete chat", "error");
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  if (isCollapsed) {
    return (
      <div className="h-full w-12 bg-surface-primary border-l border-border-primary flex flex-col items-center py-4">
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 bg-surface-secondary hover:bg-interactive-hover border border-border-secondary rounded-lg transition text-content-tertiary hover:text-content-primary"
          title="Expand history"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Clock className="w-5 h-5 text-content-quaternary rotate-90" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-80 bg-surface-primary border-l border-border-primary flex flex-col">
      {/* Header */}
      {/* Header */}
      <div className="p-4 border-b border-border-primary flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-content-tertiary" />
          <h2 className="text-content-primary font-semibold">Recent Chats</h2>
        </div>
        <div className="flex items-center gap-1">
          {availableTags.length > 0 && (
            <button
              onClick={() => setShowTagFilter((prev) => !prev)}
              className={`p-1.5 rounded-lg transition ${
                showTagFilter || activeTags.size > 0
                  ? "bg-brand-primary-subtle text-brand-primary"
                  : "hover:bg-interactive-hover text-content-tertiary hover:text-content-primary"
              }`}
              title="Filter by tags"
            >
              <Filter className="w-4 h-4" />
              {activeTags.size > 0 && <span className="sr-only">{activeTags.size} tag filters active</span>}
            </button>
          )}
          <button
            onClick={() => setIsCollapsed(true)}
            className="p-1.5 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
            title="Collapse history"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tag filter panel */}
      {showTagFilter && availableTags.length > 0 && (
        <div className="px-3 py-2 border-b border-border-primary">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-content-tertiary">Filter by tag</span>
            {activeTags.size > 0 && (
              <button
                onClick={() => setActiveTags(new Set())}
                className="text-[10px] text-content-quaternary hover:text-content-tertiary transition"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1">
            {availableTags.map((tag) => {
              const isActive = activeTags.has(tag.uuid);
              return (
                <button
                  key={tag.uuid}
                  onClick={() => toggleTag(tag.uuid)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition border ${
                    isActive ? "" : "border-border-primary text-content-tertiary hover:bg-interactive-hover"
                  }`}
                  style={
                    isActive
                      ? {
                          backgroundColor: `${tag.color}20`,
                          borderColor: `${tag.color}50`,
                          color: tag.color,
                        }
                      : undefined
                  }
                >
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                  {tag.name}
                  {isActive && <X className="w-2.5 h-2.5" />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab toggle */}
      <div className="flex border-b border-border-primary">
        <button
          onClick={() => setShowShared(false)}
          className={`flex-1 px-3 py-2 text-xs font-medium transition ${
            showShared
              ? "text-content-tertiary hover:text-content-primary"
              : "text-brand-primary border-b-2 border-brand-primary"
          }`}
        >
          My Chats
        </button>
        <button
          onClick={() => setShowShared(true)}
          className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium transition ${
            showShared
              ? "text-brand-primary border-b-2 border-brand-primary"
              : "text-content-tertiary hover:text-content-primary"
          }`}
        >
          <Users className="w-3 h-3" />
          Shared ({sharedSessions.length})
        </button>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {(() => {
          if (isLoading || (showShared && isLoadingShared)) {
            return (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
              </div>
            );
          }
          if (showShared) {
            // Shared sessions
            return sharedSessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <Users className="w-12 h-12 text-surface-tertiary mb-3" />
                <p className="text-content-tertiary text-sm">No shared chats</p>
                <p className="text-content-quaternary text-xs mt-1">Chats shared with you will appear here</p>
              </div>
            ) : (
              <div className="p-2">
                {sharedSessions.map((session) => {
                  const isActive = session.uuid === currentSessionId;
                  return (
                    <button
                      key={session.uuid}
                      onClick={() => handleSessionClick(session.uuid)}
                      className={`w-full text-left p-3 rounded-lg transition mb-1 group ${
                        isActive
                          ? "bg-brand-secondary-subtle border border-brand-secondary/30"
                          : "hover:bg-interactive-hover border border-transparent"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3
                          className={`text-sm font-medium line-clamp-2 flex-1 min-w-0 ${
                            isActive ? "text-brand-secondary" : "text-content-primary"
                          }`}
                        >
                          {session.title || "Shared Chat"}
                        </h3>
                        <Users className="w-3 h-3 text-brand-secondary shrink-0 mt-0.5" />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-content-quaternary truncate">
                          {session.sharing.sharedBy.name}
                        </span>
                        <span className="text-xs text-content-quaternary">{formatDate(session.sharing.sharedAt)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          }
          // Own sessions
          return sortedSessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <MessageSquare className="w-12 h-12 text-surface-tertiary mb-3" />
              <p className="text-content-tertiary text-sm">No chat history yet</p>
              <p className="text-content-quaternary text-xs mt-1">Start a conversation to see it here</p>
            </div>
          ) : (
            <div className="p-2">
              {sortedSessions.map((session) => {
                const isActive = session.uuid === currentSessionId;
                const isMenuOpen = menuOpenId === session.uuid;
                const isEditing = editingId === session.uuid;
                return (
                  <div key={session.uuid} className="relative mb-1">
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => !isEditing && handleSessionClick(session.uuid, session.projectUuid)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          if (!isEditing) {
                            e.preventDefault();
                            handleSessionClick(session.uuid, session.projectUuid);
                          }
                        }
                      }}
                      className={`w-full text-left p-3 rounded-lg transition group cursor-pointer ${
                        isActive
                          ? "bg-brand-primary-subtle border border-brand-primary/30"
                          : "hover:bg-interactive-hover border border-transparent"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        {isEditing ? (
                          <div
                            className="flex items-center gap-1 flex-1"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          >
                            <input
                              ref={editInputRef}
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  commitRename(session.uuid);
                                } else if (e.key === "Escape") {
                                  cancelRename();
                                }
                              }}
                              onBlur={() => commitRename(session.uuid)}
                              className="flex-1 text-sm font-medium bg-surface-secondary text-content-primary px-2 py-0.5 rounded border border-brand-primary/50 focus:outline-hidden focus:ring-1 focus:ring-brand-primary"
                              maxLength={255}
                            />
                          </div>
                        ) : (
                          <h3
                            className={`text-sm font-medium line-clamp-2 flex-1 min-w-0 ${
                              isActive ? "text-brand-primary" : "text-content-primary"
                            }`}
                            onDoubleClick={(e) => startRename(e, session)}
                            title="Double-click to rename"
                          >
                            {session.title || "New Chat"}
                          </h3>
                        )}
                        <div className="flex items-center gap-1 shrink-0">
                          {session.isStarred && (
                            <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500 group-hover:hidden" />
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpenId(isMenuOpen ? null : session.uuid);
                            }}
                            className="p-1 rounded-sm hover:bg-interactive-active text-content-quaternary hover:text-content-primary opacity-0 group-hover:opacity-100 transition-opacity"
                            title="More options"
                          >
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs text-content-quaternary">
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>{session.messageCount || 0}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {session.tags && session.tags.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              {session.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag.uuid}
                                  className="w-2 h-2 rounded-full shrink-0"
                                  style={{ backgroundColor: tag.color }}
                                  title={tag.name}
                                />
                              ))}
                              {session.tags.length > 3 && (
                                <span className="text-[10px] text-content-quaternary ml-0.5">
                                  +{session.tags.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          <span className="text-xs text-content-quaternary">
                            {formatDate(session.updatedAt || session.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    {isMenuOpen && (
                      <div className="absolute right-2 top-10 z-50 bg-surface-secondary border border-border-secondary rounded-lg shadow-xl py-1 min-w-[160px]">
                        <button
                          onClick={(e) => handleToggleStar(e, session)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-primary hover:bg-interactive-active transition"
                        >
                          <Star
                            className={`w-4 h-4 ${session.isStarred ? "text-yellow-500 fill-yellow-500" : "text-content-tertiary"}`}
                          />
                          {session.isStarred ? "Unstar" : "Star"}
                        </button>
                        <button
                          onClick={(e) => startRename(e, session)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-primary hover:bg-interactive-active transition"
                        >
                          <Pencil className="w-4 h-4" />
                          Rename
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, session)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-interactive-active hover:text-red-300 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Footer — pb-24 ensures clearance above the fixed Feedback button */}
      <div className="p-3 pb-24 border-t border-border-primary">
        <button
          onClick={() => navigate("/history")}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          View All History
        </button>
      </div>
    </div>
  );
}
